import React, { useState } from 'react';

const WeightSimulator = ({ platformId, examples }) => {
  const [selectedExample, setSelectedExample] = useState(0);
  const [sliderValues, setSliderValues] = useState({});
  
  if (!examples || examples.length === 0) return null;
  
  const currentExample = examples[selectedExample];
  const modalities = currentExample?.evidence_layers || {};
  
  const handleSliderChange = (modality, value) => {
    setSliderValues(prev => ({ ...prev, [modality]: value }));
  };

  const getBaseWeight = (modality) => {
    const features = modalities[modality] || [];
    if (features.length === 0) return 0;
    return features.reduce((sum, f) => sum + (f?.weight || 0), 0) / features.length;
  };

  const getCurrentWeight = (modality) => {
    return sliderValues[modality] !== undefined ? sliderValues[modality] : getBaseWeight(modality);
  };

  const calculateConfidence = () => {
    const keys = Object.keys(modalities);
    if (keys.length === 0) return 0.5;
    const weights = keys.map(m => getCurrentWeight(m));
    const avgWeight = weights.reduce((a, b) => a + b, 0) / weights.length;
    return Math.min(0.99, 0.5 + (avgWeight * 0.5));
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h3 className="text-sm font-semibold text-slate-700 mb-4">
        Interactive Weight Simulator
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Adjust modality weights to see how confidence changes
      </p>
      
      <div className="flex gap-2 mb-4 flex-wrap">
        {examples.map((ex, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => {
              setSelectedExample(idx);
              setSliderValues({});
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition ${
              selectedExample === idx
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {ex?.case?.length > 15 ? ex.case.substring(0, 15) + '…' : ex?.case || `Case ${idx + 1}`}
          </button>
        ))}
      </div>
      
      <div className="space-y-4 mb-6">
        {Object.entries(modalities).map(([modality, features]) => {
          const baseWeight = getBaseWeight(modality);
          const currentWeight = getCurrentWeight(modality);
          
          return (
            <div key={modality}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-slate-600 capitalize">
                  {modality}
                </span>
                <span className="text-xs text-slate-400">
                  {(currentWeight * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isNaN(currentWeight) ? 0 : currentWeight}
                onChange={(e) => handleSliderChange(modality, parseFloat(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                <span>Base: {(baseWeight * 100).toFixed(0)}%</span>
                <button 
                  type="button"
                  onClick={() => handleSliderChange(modality, baseWeight)}
                  className="text-indigo-500 hover:text-indigo-700"
                >
                  Reset
                </button>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">Calculated Confidence:</span>
          <span className="text-2xl font-bold text-indigo-600">
            {(calculateConfidence() * 100).toFixed(0)}%
          </span>
        </div>
        <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-300"
            style={{ width: `${calculateConfidence() * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default WeightSimulator;
